import React from 'react'
import {CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis} from 'recharts'
import {Rental} from '../../../common/types/rental'
import {formatDate, subDays} from '../../../common/utils/dateUtils'
import {getEarliestRentalDate, getLatestRentalDate} from '../../../common/utils/rentalUtils'
import {generateRentalsPerDayData} from './utils/generateRentalsPerDayData'

interface Props {
  endDate: string | undefined
  fromDate: string | undefined
  isPublic?: boolean
  rentals: Rental[]
  rollingAverage?: number
}

export const RentalsPerDay: React.FC<Props> = ({endDate, fromDate, isPublic = false, rentals, rollingAverage = 14}) => {
  // 2) clamp end to "yesterday" (local day key), and keep dates consistent
  const todayKey = formatDate(new Date())
  const yesterdayKey = formatDate(subDays(new Date(), 1))

  const fallbackFrom = fromDate || getEarliestRentalDate(rentals) || yesterdayKey
  const fallbackEnd = endDate || getLatestRentalDate(rentals) || yesterdayKey

  // If end lands on today (or later), clamp to yesterday
  let effectiveEnd = fallbackEnd >= todayKey ? yesterdayKey : fallbackEnd

  // If from accidentally ends up after end (e.g., very small datasets), pull it back
  let effectiveFrom = fallbackFrom > effectiveEnd ? effectiveEnd : fallbackFrom

  const data = generateRentalsPerDayData(rentals, effectiveFrom, effectiveEnd, rollingAverage)

  return (
    <LineChart
      width={1000}
      height={400}
      data={data}
      margin={{
        top: 20,
        right: 20,
        bottom: 20,
        left: 20,
      }}
    >
      <XAxis dataKey={'date'} />
      <YAxis />
      <Tooltip separator="" formatter={(value: number) => [`${value.toFixed(0)} rentals`, '']} labelFormatter={(label: string) => `${label}`} />
      <CartesianGrid stroke="#f5f5f5" />
      {!isPublic && <Line type="monotone" dataKey="numberOfRentals" stroke="#044A5A" />}
    </LineChart>
  )
}
